<script setup lang="ts">
import { computed } from "vue"
import { Bell, BellOff, Check, ChevronRight, EyeOff, Link, UserPlus } from "lucide-vue-next"
import { toast } from "vue-sonner"
import { useNotificationSettingsStore } from "~/stores/notificationSettings"
import { useHiddenChannelsStore } from "~/stores/hiddenChannels"
import { buildChannelUrl } from "~/lib/chat/channelUrl"
import HiddenChannelsList from "~/components/voice-ui/Channels/HiddenChannelsList.vue"
import {
  formatMutedUntil,
  inheritedLevelLabel,
  LEVEL_LABELS,
  MUTE_OPTIONS,
  NotificationLevel,
  NotificationTargetType,
  type MuteDurationValue,
  type NotificationLevelValue,
} from "~/lib/notifications/notificationPolicy"

// Touch version of NotificationMenuItems for the mobile Drawer (NotificationTargetDrawer.vue), laid
// out like the profile Drawer: without `section` it's the root list — «Заглушить …» and «Уведомления»
// rows, titles only — and each row opens its choices as a page sliding in from the right
// (the Drawer renders the same panel there with `section` set).
export type NotificationPanelSection = "mute" | "level"

const props = defineProps<{
  groupId: string
  target: "group" | "category" | "channel"
  targetId?: string
  categoryId?: string
  section?: NotificationPanelSection
}>()

const emit = defineEmits<{
  (e: "open", section: NotificationPanelSection): void
  (e: "back"): void
  /** «Пригласить» — the Drawer opens the invite dialog. */
  (e: "invite"): void
  /** The Drawer should close (the channel was just hidden, or its link copied). */
  (e: "close"): void
}>()

const store = useNotificationSettingsStore()

const targetType = computed(() => props.target === "group"
  ? NotificationTargetType.Group
  : props.target === "category" ? NotificationTargetType.Category : NotificationTargetType.Channel)
const id = computed(() => props.target === "group" ? props.groupId : (props.targetId ?? ""))

const resolved = computed(() => {
  if (props.target === "group") return store.resolveGroup(props.groupId)
  if (props.target === "category") return store.resolveCategory(props.groupId, id.value)
  return store.resolveChannel(props.groupId, id.value, props.categoryId)
})

const MUTE_TITLES = { group: "Заглушить группу", category: "Заглушить категорию", channel: "Заглушить канал" } as const
const UNMUTE_TITLES = { group: "Включить оповещения группы", category: "Включить оповещения категории", channel: "Включить оповещения канала" } as const
const muteTitle = computed(() => MUTE_TITLES[props.target])
const unmuteTitle = computed(() => UNMUTE_TITLES[props.target])

// The «Заглушить …» row: muted on its own, it turns into «Включить оповещения …» and unmutes right away.
function onMuteRow() {
  // Stays on the current page — the row itself flips back to «Заглушить …».
  if (resolved.value.ownMuted) void run(() => store.unmute(props.groupId, targetType.value, id.value))
  else emit("open", "mute")
}

const parentMutedHint = computed(() => {
  if (resolved.value.ownMuted || !resolved.value.muted) return ""
  return resolved.value.mutedBy === "group" ? "Группа заглушена — уведомлений не будет" : "Категория заглушена — уведомлений не будет"
})

const selectedLevel = computed(() => props.target === "group" ? resolved.value.level : resolved.value.ownLevel)
const levelOptions = computed(() => {
  const options: { value: NotificationLevelValue, label: string }[] = []
  if (props.target !== "group") options.push({ value: NotificationLevel.Inherit, label: inheritedLevelLabel(resolved.value) })
  for (const level of [NotificationLevel.AllMessages, NotificationLevel.OnlyMentions, NotificationLevel.Nothing] as const) {
    options.push({ value: level, label: LEVEL_LABELS[level] })
  }
  return options
})

// --- Channel actions (root list of a channel) -----------------------------------------------------
const hiddenChannels = useHiddenChannelsStore()

// Copies and closes the Drawer — the toast confirms it.
async function copyLink() {
  emit("close")
  try {
    await navigator.clipboard.writeText(buildChannelUrl(props.groupId, id.value))
    toast.success("Ссылка скопирована")
  } catch {
    toast.error("Не удалось скопировать ссылку")
  }
}

function hideChannel() {
  const groupId = props.groupId
  const channelId = id.value
  hiddenChannels.hide(groupId, channelId)
  emit("close")
  toast.success("Канал скрыт на этом устройстве", {
    description: "Вернуть его можно долгим тапом по категории или по пустому месту в списке каналов.",
    action: { label: "Вернуть", onClick: () => hiddenChannels.show(groupId, [channelId]) },
  })
}


async function run(action: () => Promise<unknown>) {
  try {
    await action()
  } catch {
    toast.error("Не удалось изменить настройки уведомлений")
  }
}

// Muting returns to the root list.
function mute(duration: MuteDurationValue) {
  emit("back")
  void run(() => store.mute(props.groupId, targetType.value, id.value, duration))
}

function unmute() {
  emit("back")
  void run(() => store.unmute(props.groupId, targetType.value, id.value))
}

function setLevel(level: NotificationLevelValue) {
  if (level === selectedLevel.value) return
  void run(() => store.setLevel(props.groupId, targetType.value, id.value, level))
}
</script>

<template>
  <!-- Root: one row per setting; each opens its own page. -->
  <div v-if="!section" class="space-y-2">
    <!-- A channel's own actions come first, then its notification rows. -->
    <div v-if="target === 'channel'" class="mx-4 mb-3 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-invite-row"
          @click="emit('invite')"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <UserPlus class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Пригласить</span>
      </button>
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-hide-row"
          @click="hideChannel"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <EyeOff class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Скрыть канал</span>
      </button>
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-copy-link-row"
          @click="copyLink"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Link class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Копировать ссылку</span>
      </button>
    </div>

    <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-mute-row"
          @click="onMuteRow"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Bell v-if="resolved.ownMuted" class="size-5"/>
          <BellOff v-else class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">{{ resolved.ownMuted ? unmuteTitle : muteTitle }}</span>
        <ChevronRight v-if="!resolved.ownMuted" class="size-5 shrink-0 text-muted-foreground/70"/>
      </button>
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-level-row"
          @click="emit('open', 'level')"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Bell class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Уведомления</span>
        <ChevronRight class="size-5 shrink-0 text-muted-foreground/70"/>
      </button>
    </div>
    <p v-if="parentMutedHint" class="px-4 text-xs text-muted-foreground">{{ parentMutedHint }}</p>

    <!-- A category's hidden channels themselves, each with «Показать». -->
    <HiddenChannelsList v-if="target === 'category'" class="!mt-4" :group-id="groupId" :category-id="id"/>
  </div>

  <div v-else-if="section === 'mute'" class="space-y-2">
    <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
      <button
          v-if="resolved.ownMuted"
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          @click="unmute"
      >
        <Bell class="size-5 shrink-0 text-muted-foreground"/>
        <span class="grid min-w-0 flex-1 leading-tight">
          <span class="truncate text-[15px] font-medium">Включить звук</span>
          <span class="truncate text-xs text-muted-foreground">{{ formatMutedUntil(resolved.ownMutedUntil, store.now) }}</span>
        </span>
      </button>
      <template v-else>
        <button
            v-for="option in MUTE_OPTIONS"
            :key="option.value"
            type="button"
            class="flex min-h-12 w-full items-center px-4 py-2.5 text-left text-[15px] transition-colors active:bg-muted"
            @click="mute(option.value)"
        >
          {{ option.label }}
        </button>
      </template>
    </div>
    <p v-if="parentMutedHint" class="px-4 text-xs text-muted-foreground">{{ parentMutedHint }}</p>
  </div>

  <!-- «Заглушить …» repeated at the top of the level page, so it's one tap away from here too. -->
  <div v-else class="space-y-3">
    <div class="mx-4 overflow-hidden rounded-2xl border bg-muted/40">
      <button
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          data-testid="notification-panel-level-mute-row"
          @click="onMuteRow"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Bell v-if="resolved.ownMuted" class="size-5"/>
          <BellOff v-else class="size-5"/>
        </span>
        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">{{ resolved.ownMuted ? unmuteTitle : muteTitle }}</span>
        <ChevronRight v-if="!resolved.ownMuted" class="size-5 shrink-0 text-muted-foreground/70"/>
      </button>
    </div>
    <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40" role="radiogroup" aria-label="Уведомления">
      <button
          v-for="option in levelOptions"
          :key="option.value"
          type="button"
          role="radio"
          :aria-checked="selectedLevel === option.value"
          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
          @click="setLevel(option.value)"
      >
        <span class="min-w-0 flex-1 truncate text-[15px]">{{ option.label }}</span>
        <Check v-if="selectedLevel === option.value" class="size-5 shrink-0 text-primary"/>
      </button>
    </div>
  </div>
</template>
